import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { featureFlagsAPI } from '../services/api';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { Rocket, ArrowLeft, EyeSlash, Flask, Globe, CaretDown, CaretRight } from '@phosphor-icons/react';

const PHASE_META = {
  1: { title: 'Phase 1 — Core GTM Engine', color: '#22C55E', desc: 'Profiles, marketplace, wallet, P2P, DiviSpend investing, payment rails + fraud & GDPR compliance hooks.' },
  2: { title: 'Phase 2 — Trust & Retention', color: '#3B82F6', desc: 'SMS 2FA, CAPTCHA, email receipts, PDF invoices, in-app notification center.' },
  5: { title: 'Phase 5 — Post-GTM Backlog', color: '#64748B', desc: 'Monetization, theming, CRM, widgets, analytics — frozen until core product demand is proven.' },
};

const PHASES = [1, 2, 5];

export default function AdminFeaturesPage() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [confirmPhase, setConfirmPhase] = useState(null);
  // Phase 1 expanded by default; others collapsed so all phases fit on screen
  const [expandedPhases, setExpandedPhases] = useState({ 1: true, 2: false, 5: false });
  const { refresh: refreshFlags } = useFeatureFlags();

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const expandAll = () => setExpandedPhases({ 1: true, 2: true, 3: true, 4: true, 5: true });
  const collapseAll = () => setExpandedPhases({ 1: false, 2: false, 3: false, 4: false, 5: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await featureFlagsAPI.adminListAll();
      setFlags(data.flags || []);
    } catch (e) {
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = { 1: [], 2: [], 5: [] };
    flags.forEach(f => { if (map[f.phase]) map[f.phase].push(f); });
    return map;
  }, [flags]);

  const stats = useMemo(() => {
    const live = flags.filter(f => f.enabled && !f.beta_only).length;
    const beta = flags.filter(f => f.enabled && f.beta_only).length;
    const off = flags.filter(f => !f.enabled).length;
    return { live, beta, off, total: flags.length };
  }, [flags]);

  const updateFlag = async (key, patch) => {
    setSavingKey(key);
    try {
      const { data } = await featureFlagsAPI.adminUpdate(key, patch);
      setFlags(prev => prev.map(f => f.key === key ? data : f));
      await refreshFlags();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingKey(null);
    }
  };

  const launchPhase = async (phase, enabled, beta_only) => {
    try {
      await featureFlagsAPI.adminLaunchPhase(phase, enabled, beta_only);
      const label = enabled ? (beta_only ? 'released to beta' : 'launched to public') : 'paused';
      toast.success(`Phase ${phase} ${label}`);
      await load();
      await refreshFlags();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Launch failed');
    } finally {
      setConfirmPhase(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white" data-testid="admin-features-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/dashboard" className="text-[#999999] hover:text-[#22C55E] flex items-center gap-2 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-2">Feature Control</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Feature Flags & Phase Launch
            </h1>
            <p className="text-[#999999] mt-2 max-w-xl text-sm">
              Toggle individual features, drop them into different phases, or launch an entire phase to public/beta with one click.
            </p>
          </div>
          <div className="flex gap-3 text-xs items-center">
            <StatPill icon={<Globe className="w-3.5 h-3.5" />} label="Live" value={stats.live} color="#22C55E" />
            <StatPill icon={<Flask className="w-3.5 h-3.5" />} label="Beta" value={stats.beta} color="#3B82F6" />
            <StatPill icon={<EyeSlash className="w-3.5 h-3.5" />} label="Hidden" value={stats.off} color="#666666" />
            <Button size="sm" variant="ghost" className="text-[#999] hover:text-white text-xs h-7 px-2" onClick={expandAll} data-testid="expand-all-btn">Expand all</Button>
            <Button size="sm" variant="ghost" className="text-[#999] hover:text-white text-xs h-7 px-2" onClick={collapseAll} data-testid="collapse-all-btn">Collapse all</Button>
          </div>
        </div>

        {/* Sticky phase nav — jump links */}
        {!loading && (
          <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-[#111111]/95 backdrop-blur-sm border-y border-[#222] mb-6">
            <div className="flex items-center gap-2 overflow-x-auto text-xs">
              <span className="text-[#666] uppercase tracking-wider font-semibold mr-2 whitespace-nowrap">Jump to:</span>
              {PHASES.map(p => {
                const items = grouped[p] || [];
                const liveCount = items.filter(i => i.enabled && !i.beta_only).length;
                const betaCount = items.filter(i => i.enabled && i.beta_only).length;
                return (
                  <a
                    key={p}
                    href={`#phase-${p}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setExpandedPhases(prev => ({ ...prev, [p]: true }));
                      setTimeout(() => document.getElementById(`phase-${p}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#222] hover:border-[#22C55E]/40 whitespace-nowrap transition-colors"
                    data-testid={`jump-phase-${p}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: PHASE_META[p].color }} />
                    <span className="font-semibold text-white">Phase {p}</span>
                    <span className="text-[#666]">{liveCount}L · {betaCount}B · {items.length}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-[#999999]">Loading flags…</p>
        ) : (
          <div className="space-y-4">
            {PHASES.map(phase => (
              <PhaseSection
                key={phase}
                phase={phase}
                meta={PHASE_META[phase]}
                items={grouped[phase] || []}
                expanded={!!expandedPhases[phase]}
                onToggleExpand={() => togglePhase(phase)}
                onToggleEnabled={(key, enabled) => updateFlag(key, { enabled })}
                onToggleBeta={(key, beta_only) => updateFlag(key, { beta_only })}
                onChangePhase={(key, newPhase) => updateFlag(key, { phase: newPhase })}
                onLaunch={(enabled, beta_only) => setConfirmPhase({ phase, enabled, beta_only })}
                savingKey={savingKey}
              />
            ))}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirmPhase && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="phase-launch-modal">
          <div className="bg-[#1a1a1a] border border-[#333] rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>Confirm phase action</h3>
            <p className="text-sm text-[#999] mb-6">
              You're about to {confirmPhase.enabled ? (confirmPhase.beta_only ? 'release to BETA users' : 'GO LIVE for the public') : 'pause'}{' '}
              all features in <strong className="text-white">Phase {confirmPhase.phase}</strong>. This affects every visitor immediately.
            </p>
            <div className="flex gap-3">
              <Button variant="ghost" className="flex-1" onClick={() => setConfirmPhase(null)} data-testid="phase-cancel-btn">
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white"
                onClick={() => launchPhase(confirmPhase.phase, confirmPhase.enabled, confirmPhase.beta_only)}
                data-testid="phase-confirm-btn"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2 bg-[#1a1a1a] border border-[#222] rounded-full px-3 py-1.5">
      <span style={{ color }}>{icon}</span>
      <span className="text-[#999]">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
  );
}

function PhaseSection({ phase, meta, items, expanded, onToggleExpand, onToggleEnabled, onToggleBeta, onChangePhase, onLaunch, savingKey }) {
  const liveCount = items.filter(i => i.enabled && !i.beta_only).length;
  const betaCount = items.filter(i => i.enabled && i.beta_only).length;

  return (
    <section id={`phase-${phase}`} className="border border-[#222] rounded-xl overflow-hidden scroll-mt-24" data-testid={`phase-${phase}-section`}>
      <div className="px-5 py-4 border-b border-[#222] bg-[#0d0d0d] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <button
          onClick={onToggleExpand}
          className="flex items-start gap-3 text-left flex-1 group"
          data-testid={`phase-${phase}-toggle`}
        >
          <span className="mt-1 text-[#666] group-hover:text-white transition-colors">
            {expanded ? <CaretDown className="w-4 h-4" /> : <CaretRight className="w-4 h-4" />}
          </span>
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>{meta.title}</h2>
              <span className="text-xs text-[#666]">
                {liveCount} live · {betaCount} beta · {items.length} total
              </span>
            </div>
            <p className="text-xs text-[#999]">{meta.desc}</p>
          </div>
        </button>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="bg-[#3B82F6]/20 hover:bg-[#3B82F6]/30 text-[#3B82F6] border border-[#3B82F6]/30 rounded-sm text-xs"
            onClick={() => onLaunch(true, true)}
            data-testid={`phase-${phase}-beta-btn`}
          >
            <Flask className="w-3.5 h-3.5 mr-1.5" /> Release to Beta
          </Button>
          <Button
            size="sm"
            className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-xs"
            onClick={() => onLaunch(true, false)}
            data-testid={`phase-${phase}-launch-btn`}
          >
            <Rocket className="w-3.5 h-3.5 mr-1.5" /> Launch Public
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-[#999] hover:text-white text-xs rounded-sm"
            onClick={() => onLaunch(false, false)}
            data-testid={`phase-${phase}-pause-btn`}
          >
            <EyeSlash className="w-3.5 h-3.5 mr-1.5" /> Pause All
          </Button>
        </div>
      </div>

      {expanded && (
        items.length === 0 ? (
          <p className="px-5 py-6 text-sm text-[#666]">No features in this phase yet.</p>
        ) : (
          <div className="divide-y divide-[#1a1a1a]">
            {items.map(item => (
              <FlagRow
                key={item.key}
                item={item}
                saving={savingKey === item.key}
                onToggleEnabled={onToggleEnabled}
                onToggleBeta={onToggleBeta}
                onChangePhase={onChangePhase}
              />
            ))}
          </div>
        )
      )}
    </section>
  );
}

function FlagRow({ item, saving, onToggleEnabled, onToggleBeta, onChangePhase }) {
  const statusBadge = !item.enabled
    ? { label: 'Hidden', color: '#666' }
    : item.beta_only
      ? { label: 'Beta Only', color: '#3B82F6' }
      : { label: 'Live', color: '#22C55E' };

  return (
    <div className="px-5 py-4 hover:bg-[#0d0d0d] transition-colors" data-testid={`flag-row-${item.key}`}>
      <div className="flex flex-col md:flex-row md:items-center md:gap-6 gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
              style={{ background: `${statusBadge.color}20`, color: statusBadge.color }}
            >
              {statusBadge.label}
            </span>
            <h4 className="font-semibold text-white">{item.name}</h4>
            <span className="text-[11px] text-[#555]">{item.category}</span>
          </div>
          <p className="text-xs text-[#999] truncate">{item.description}</p>
          <p className="text-[10px] text-[#444] mt-1">key: {item.key}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-[#999]">
            Enabled
            <Switch
              checked={item.enabled}
              disabled={saving}
              onCheckedChange={(v) => onToggleEnabled(item.key, v)}
              data-testid={`enable-${item.key}`}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-[#999]">
            Beta Only
            <Switch
              checked={item.beta_only}
              disabled={saving || !item.enabled}
              onCheckedChange={(v) => onToggleBeta(item.key, v)}
              data-testid={`beta-${item.key}`}
            />
          </label>
          <Select
            value={String(item.phase)}
            onValueChange={(v) => onChangePhase(item.key, parseInt(v, 10))}
            disabled={saving}
          >
            <SelectTrigger className="w-40 bg-[#0a0a0a] border-[#222] text-xs h-8" data-testid={`phase-select-${item.key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#333]">
              <SelectItem value="1">Phase 1 — GTM</SelectItem>
              <SelectItem value="2">Phase 2 — Trust</SelectItem>
              <SelectItem value="5">Phase 5 — Backlog</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
